import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Button, ButtonVariant, Modal, ModalSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";

import { type CropArea, getCroppedBase64 } from "@/components/AvatarGeneratorModal/utils/imageCrop";
import Trans from "@/components/Trans/Trans";

const logger = appLogger.withTag("ImageCropModal");

interface IImageCropModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  imageType: string;
  onCropComplete: (croppedBase64: string) => void;
}

export const ImageCropModal = ({ open, onOpenChange, imageSrc, imageType, onCropComplete }: IImageCropModalProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropChange = useCallback((location: { x: number; y: number }) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropAreaChange = useCallback((_croppedArea: CropArea, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;

    setIsCropping(true);
    try {
      const croppedBase64 = await getCroppedBase64(imageSrc, croppedAreaPixels, imageType);
      onCropComplete(croppedBase64);
      onOpenChange(false);
    } catch (error) {
      logger.error("Error cropping image:", error);
    } finally {
      setIsCropping(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} size={ModalSize.LARGE} className="bg-black/95">
      <div className="flex flex-col gap-4">
        <Text variant={TextVariants.HEADING_5_BOLD}>
          <Trans i18nKey="common:modal.generateAvatar.cropImage.title" />
        </Text>

        <div className="relative w-full h-[400px] md:h-[500px] bg-gray-9 rounded-md overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropAreaChange}
            style={{
              containerStyle: {
                backgroundColor: "#1a1a1a",
              },
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM}>
            <Trans i18nKey="common:modal.generateAvatar.cropImage.zoom" />
          </Text>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e: { target: { value: string } }) => setZoom(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant={ButtonVariant.OUTLINED} onClick={handleCancel} disabled={isCropping}>
            <Trans i18nKey="common:cancel" />
          </Button>
          <Button variant={ButtonVariant.CONTAINED} onClick={handleCrop} disabled={isCropping || !croppedAreaPixels}>
            {isCropping ? (
              <Trans i18nKey="common:modal.generateAvatar.cropImage.cta.cropping" />
            ) : (
              <Trans i18nKey="common:modal.generateAvatar.cropImage.cta.apply" />
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
